import { cloneForRead, hydrateValue, OfflineTimestamp, serializeValue, timestampToComparable } from './timestamp';
import { seedData } from './seedData';

const DB_KEY = 'ybit-offline-firestore-v1';
const DB_VERSION_KEY = 'ybit-offline-firestore-version';
const SEED_VERSION = '2026-05-13.3';

const randomId = () => `offline_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

let driveStoreCache = null;
let driveStoreLoadPromise = null;

const normalizeSegment = (segment) => String(segment).replace(/^\/+|\/+$/g, '');

const joinPath = (...segments) => segments.flat().filter(Boolean).map(normalizeSegment).filter(Boolean).join('/');

const isCollectionRef = (value) => value && value.__type === 'collection';
const isDocumentRef = (value) => value && value.__type === 'doc';

const shouldUseDriveDatabase = () => (
  typeof window !== 'undefined' &&
  process.env.REACT_APP_STORAGE_MODE === 'drive'
);

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return window.location.origin;
  }

  return process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
};

const getDatabaseUrl = () => `${getApiBaseUrl().replace(/\/$/, '')}/api/db`;

const getStoredSessionToken = () => {
  if (typeof window === 'undefined') return '';

  const sessionKeys = ['userSession', 'studentSession', 'adminSession'];
  for (const key of sessionKeys) {
    const rawSession = window.sessionStorage.getItem(key);
    if (!rawSession) continue;

    try {
      const session = JSON.parse(rawSession);
      if (session?.sessionToken) return session.sessionToken;
    } catch (error) {
      console.warn(`Could not parse ${key}.`, error);
    }
  }

  return '';
};

const getAuthHeaders = () => {
  const token = getStoredSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isEmptyStore = (store) => !store || Object.keys(store).length === 0;

const loadLocalStore = () => {
  if (typeof window === 'undefined') return cloneForRead(seedData);

  const existing = window.localStorage.getItem(DB_KEY);
  const existingVersion = window.localStorage.getItem(DB_VERSION_KEY);
  if (existing && existingVersion === SEED_VERSION) {
    try {
      return hydrateValue(JSON.parse(existing));
    } catch (error) {
      console.warn('Could not read offline database, reseeding.', error);
    }
  }

  const seeded = cloneForRead(seedData);
  saveLocalStore(seeded);
  window.localStorage.setItem(DB_VERSION_KEY, SEED_VERSION);
  return seeded;
};

const saveLocalStore = (store) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(DB_KEY, JSON.stringify(serializeValue(store)));
};

const saveDriveStore = async (store) => {
  driveStoreCache = cloneForRead(store);

  const response = await fetch(getDatabaseUrl(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ data: serializeValue(store) })
  });

  const result = await response.json().catch(() => ({}));

  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Could not save Google Drive database');
  }
};

const loadDriveStore = async () => {
  if (driveStoreCache) return cloneForRead(driveStoreCache);
  if (driveStoreLoadPromise) return driveStoreLoadPromise;

  driveStoreLoadPromise = (async () => {
    const response = await fetch(getDatabaseUrl(), {
      headers: {
        Accept: 'application/json',
        ...getAuthHeaders()
      }
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Could not load Google Drive database');
    }

    const loaded = hydrateValue(result.data || {});
    const store = isEmptyStore(loaded) ? loadLocalStore() : loaded;

    driveStoreCache = cloneForRead(store);

    if (isEmptyStore(loaded)) {
      await saveDriveStore(store);
    }

    return cloneForRead(store);
  })().finally(() => {
    driveStoreLoadPromise = null;
  });

  return driveStoreLoadPromise;
};

const loadRawStore = async () => {
  if (shouldUseDriveDatabase()) {
    return loadDriveStore();
  }

  return loadLocalStore();
};

const saveStore = async (store) => {
  if (shouldUseDriveDatabase()) {
    await saveDriveStore(store);
    return;
  }

  saveLocalStore(store);
};

const ensureCollection = (store, collectionPath) => {
  if (!store[collectionPath]) store[collectionPath] = {};
  return store[collectionPath];
};

const splitDocPath = (path) => {
  const parts = joinPath(path).split('/').filter(Boolean);
  const id = parts.pop();
  return {
    collectionPath: parts.join('/'),
    id
  };
};

const readNested = (value, path) => {
  return String(path).split('.').reduce((current, key) => current?.[key], value);
};

const applyArrayUnion = (current, values) => {
  const existing = Array.isArray(current) ? current : [];
  const keys = new Set(existing.map((item) => JSON.stringify(serializeValue(item))));
  const next = [...existing];

  values.forEach((item) => {
    const key = JSON.stringify(serializeValue(item));
    if (!keys.has(key)) {
      keys.add(key);
      next.push(item);
    }
  });

  return next;
};

const applyUpdate = (target, updates) => {
  const next = { ...target };

  Object.entries(updates).forEach(([key, value]) => {
    if (value && value.__op === 'arrayUnion') {
      next[key] = applyArrayUnion(next[key], value.values);
    } else {
      next[key] = value;
    }
  });

  return next;
};

class OfflineDocumentSnapshot {
  constructor(ref, data) {
    this.ref = ref;
    this.id = ref.id;
    this._data = data ? cloneForRead(data) : null;
  }

  exists() {
    return Boolean(this._data);
  }

  data() {
    return this._data ? cloneForRead(this._data) : undefined;
  }
}

class OfflineQuerySnapshot {
  constructor(docs) {
    this.docs = docs;
    this.empty = docs.length === 0;
    this.size = docs.length;
  }

  forEach(callback) {
    this.docs.forEach(callback);
  }
}

export const getFirestore = () => ({ __type: 'db', offline: true });

export const collection = (base, ...segments) => {
  const basePath = isCollectionRef(base) || isDocumentRef(base) ? base.path : '';
  return {
    __type: 'collection',
    path: joinPath(basePath, segments)
  };
};

export const doc = (base, ...segments) => {
  if (isCollectionRef(base) && segments.length === 0) {
    segments = [randomId()];
  }

  const basePath = isCollectionRef(base) || isDocumentRef(base) ? base.path : '';
  const path = joinPath(basePath, segments);
  const { collectionPath, id } = splitDocPath(path);

  return {
    __type: 'doc',
    path,
    collectionPath,
    id
  };
};

export const getDoc = async (docRef) => {
  const store = await loadRawStore();
  const data = store[docRef.collectionPath]?.[docRef.id];
  return new OfflineDocumentSnapshot(docRef, data);
};

export const setDoc = async (docRef, data, options = {}) => {
  const store = await loadRawStore();
  const coll = ensureCollection(store, docRef.collectionPath);
  const current = coll[docRef.id] || {};
  coll[docRef.id] = options.merge ? { ...current, ...cloneForRead(data) } : cloneForRead(data);
  await saveStore(store);
};

export const updateDoc = async (docRef, updates) => {
  const store = await loadRawStore();
  const coll = ensureCollection(store, docRef.collectionPath);
  const current = coll[docRef.id] || {};
  coll[docRef.id] = applyUpdate(current, cloneForRead(updates));
  await saveStore(store);
};

export const deleteDoc = async (docRef) => {
  const store = await loadRawStore();
  if (store[docRef.collectionPath]) {
    delete store[docRef.collectionPath][docRef.id];
    await saveStore(store);
  }
};

export const addDoc = async (collectionRef, data) => {
  const newRef = doc(collectionRef, randomId());
  await setDoc(newRef, data);
  return newRef;
};

export const where = (field, op, value) => ({ __constraint: 'where', field, op, value });
export const orderBy = (field, direction = 'asc') => ({ __constraint: 'orderBy', field, direction });
export const limit = (count) => ({ __constraint: 'limit', count });

export const query = (collectionRef, ...constraints) => ({
  __type: 'query',
  collectionRef,
  constraints
});

const matchesWhere = (data, constraint) => {
  const actual = readNested(data, constraint.field);
  const expected = constraint.value;

  switch (constraint.op) {
    case '==':
      return actual === expected;
    case '!=':
      return actual !== expected;
    case 'in':
      return Array.isArray(expected) && expected.includes(actual);
    case 'array-contains':
      return Array.isArray(actual) && actual.includes(expected);
    case '>':
      return actual > expected;
    case '>=':
      return actual >= expected;
    case '<':
      return actual < expected;
    case '<=':
      return actual <= expected;
    default:
      return true;
  }
};

export const getDocs = async (target) => {
  const store = await loadRawStore();
  const collectionRef = target.__type === 'query' ? target.collectionRef : target;
  const constraints = target.__type === 'query' ? target.constraints : [];
  const docs = Object.entries(store[collectionRef.path] || {}).map(([id, data]) => {
    const ref = doc(collectionRef, id);
    return new OfflineDocumentSnapshot(ref, data);
  });

  let rows = docs;

  constraints.filter((item) => item.__constraint === 'where').forEach((constraint) => {
    rows = rows.filter((snapshot) => matchesWhere(snapshot.data(), constraint));
  });

  constraints.filter((item) => item.__constraint === 'orderBy').forEach((constraint) => {
    rows = [...rows].sort((a, b) => {
      const left = timestampToComparable(readNested(a.data(), constraint.field));
      const right = timestampToComparable(readNested(b.data(), constraint.field));
      const result = left > right ? 1 : left < right ? -1 : 0;
      return constraint.direction === 'desc' ? -result : result;
    });
  });

  const limitConstraint = constraints.find((item) => item.__constraint === 'limit');
  if (limitConstraint) {
    rows = rows.slice(0, limitConstraint.count);
  }

  return new OfflineQuerySnapshot(rows);
};

export const writeBatch = () => {
  const operations = [];

  return {
    set: (ref, data, options = {}) => operations.push({ type: 'set', ref, data, options }),
    update: (ref, data) => operations.push({ type: 'update', ref, data }),
    delete: (ref) => operations.push({ type: 'delete', ref }),
    commit: async () => {
      const store = await loadRawStore();

      operations.forEach((operation) => {
        if (operation.type === 'set') {
          const coll = ensureCollection(store, operation.ref.collectionPath);
          const current = coll[operation.ref.id] || {};
          coll[operation.ref.id] = operation.options.merge
            ? { ...current, ...cloneForRead(operation.data) }
            : cloneForRead(operation.data);
        }

        if (operation.type === 'update') {
          const coll = ensureCollection(store, operation.ref.collectionPath);
          const current = coll[operation.ref.id] || {};
          coll[operation.ref.id] = applyUpdate(current, cloneForRead(operation.data));
        }

        if (operation.type === 'delete' && store[operation.ref.collectionPath]) {
          delete store[operation.ref.collectionPath][operation.ref.id];
        }
      });

      await saveStore(store);
    }
  };
};

export const onSnapshot = (target, callback) => {
  getDocs(target).then(callback);
  return () => {};
};

export const serverTimestamp = () => OfflineTimestamp.now();
export const Timestamp = OfflineTimestamp;
export const arrayUnion = (...values) => ({ __op: 'arrayUnion', values });

export const __resetOfflineFirestore = () => {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(DB_KEY);
  }
};
